import { useEffect, useState } from 'react';
import { TOPIC_TAGS } from '../../lib/constants';
import { useI18n } from '../../i18n';
import { Bars, Pill, StatTile, Suppressed } from '../../ui/DataBits';
import { MoodSplit } from '../../ui/MoodSplit';
import { bandOf } from '../../ui/score';
import { errorText, rpc, type AppError } from '../admin/api';
import { SignalsPanel, ThlPanel } from './R3Panels';
import { useSchoolDates } from './useSchoolDates';

export type Window =
  | { status: 'ok'; total: number; mean: number; mood_mode?: 'faces' | 'bands' | 'hidden'; mood_counts?: Record<string, number> | null; mood_bands?: { low: number; mid: number; high: number } | null; topic_counts?: Record<string, number | null> }
  | { status: 'insufficient' | 'insufficient_spread' | 'suppressed_near_unanimous' | 'hidden_complement'; total: number; floor?: number; mean?: null };

export type GradeWindow = Window & { grade_id: string; label: string };

type Insights = {
  scope_grades: string[] | null;
  weeks: { from: string; to: string; school: Window | null; grades: GradeWindow[] }[];
  participation: { grade_id: string; label: string; sessions: number; responses: number; rate: number | null }[];
  inactive_classes: number;
  context_tags: Record<string, number>;
  actions: Record<string, number>;
  replies: number;
  month_from: string;
  month_to: string;
};

// H3/H4: grade and school results by complete week, participation, and
// counts of what was going on and what was tried. Never a class.
export function HeadInsights() {
  const { t, locale } = useI18n();
  const [data, setData] = useState<Insights | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const dates = useSchoolDates();
  useEffect(() => {
    rpc<Insights>('head_insights', { p_weeks: 8 }).then((r) => (r.error ? setErr(r.error) : setData(r.data)));
  }, []);

  if (err) return <p className="notice notice-block" role="alert">{errorText(t, err)}</p>;
  if (!data) return <p role="status">{t.loading}</p>;

  const dfmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric' });
  const last = data.weeks[data.weeks.length - 1];
  // B14: no weeks from before the school existed.
  const created = dates ? dates.created_at.slice(0, 10) : null;
  const weeks = created ? data.weeks.filter((w) => w.to >= created) : data.weeks;
  const notOpen = !!dates && dates.state !== 'live';
  const whole = data.scope_grades === null;
  const lastSchool = last?.school;
  const gradeLabels = last?.grades.map((g) => g.label) ?? [];

  return (
    <>
      <p className="notice notice-block">
        {whole ? t['h2.scopeWhole'] : t['h2.scopeGrades']((data.scope_grades ?? []).join(', '))} {t['h2.neverClass']}
      </p>
      <div className="stat-row">
        <StatTile
          label={t['h2.lastWeek']}
          score={whole && lastSchool?.status === 'ok' ? lastSchool.mean : null}
          sub={whole ? (lastSchool?.status === 'ok' ? t[`band.${bandOf(lastSchool.mean).key}`] : t['h2.notShown']) : t['h2.scopedNoTotal']}
        />
        <StatTile label={t['h2.answersLastWeek']} value={whole && lastSchool ? lastSchool.total : '–'} sub={`${dfmt.format(new Date(last.from))}–${dfmt.format(new Date(last.to))}`} />
        <StatTile label={t['h2.inactive']} value={notOpen ? '–' : data.inactive_classes} sub={notOpen ? t['r4.notOpenYet'] : t['h2.inactiveSub']} />
        <StatTile label={t['h2.replies']} value={data.replies} sub={t['h2.last4']} />
      </div>

      <section className="panel p5" aria-labelledby="wk-title">
        <h2 className="panel-title" id="wk-title">{t['h2.weeksTitle']}</h2>
        <p className="form-hint">{t['h2.weeksHint']}</p>
        <div className="table-wrap" tabIndex={0} role="region" aria-labelledby="wk-title">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t['h2.week']}</th>
                {whole && <th scope="col">{t['h2.school']}</th>}
                {gradeLabels.map((g) => (
                  <th scope="col" key={g}>{t['h2.grade'](g)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...weeks].reverse().map((w) => (
                <tr key={w.from}>
                  <th scope="row">{`${dfmt.format(new Date(w.from))}–${dfmt.format(new Date(w.to))}`}</th>
                  {whole && <td><WindowCell w={w.school} /></td>}
                  {w.grades.map((g) => (
                    <td key={g.grade_id}><WindowCell w={g} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="bars-note">{t['h2.cellNote']}</p>
      </section>

      {whole && lastSchool && (
        <div className="two-col">
          <section className="panel p5" aria-labelledby="lw-mood">
            <h2 className="panel-title" id="lw-mood">{t['h2.lastWeekMood']}</h2>
            {lastSchool.status === 'ok' ? (
              <MoodSplit labelledBy="lw-mood" result={lastSchool} />
            ) : (
              <Suppressed title={t['sup.insufficientTitle']}>{t['h2.windowHidden']}</Suppressed>
            )}
          </section>
          <section className="panel p5" aria-labelledby="lw-topics">
            <h2 className="panel-title" id="lw-topics">{t['h2.lastWeekTopics']}</h2>
            {lastSchool.status === 'ok' && lastSchool.topic_counts ? (
              <Bars
                labelledBy="lw-topics"
                total={lastSchool.total}
                data={TOPIC_TAGS.map((tg) => ({ key: tg.value, emoji: tg.emoji, label: t[`topic.${tg.value}` as 'topic.exams'], count: lastSchool.topic_counts?.[tg.value] ?? null }))}
              />
            ) : (
              <Suppressed title={t['sup.insufficientTitle']}>{t['h2.windowHidden']}</Suppressed>
            )}
          </section>
        </div>
      )}

      <div className="two-col">
        <section className="panel p5" aria-labelledby="part-title">
          <h2 className="panel-title" id="part-title">{t['h2.partTitle']}</h2>
          <p className="form-hint">{t['h2.partHint'](dfmt.format(new Date(data.month_from)), dfmt.format(new Date(data.month_to)))}</p>
          <dl className="kv">
            {data.participation.map((p) => (
              <div key={p.grade_id}>
                <dt>{t['h2.grade'](p.label)}</dt>
                <dd>
                  {t['h2.partLine'](p.sessions, p.responses)}
                  {p.rate !== null && <> · {t['h2.rate'](Math.round(p.rate * 100))}</>}
                </dd>
              </div>
            ))}
          </dl>
        </section>
        <section className="panel p5" aria-labelledby="ctx-title">
          <h2 className="panel-title" id="ctx-title">{t['h2.ctxTitle']}</h2>
          <p className="form-hint">{t['h2.ctxHint']}</p>
          <dl className="kv">
            {Object.entries(data.context_tags).map(([k, n]) => (
              <div key={k}><dt>{t[`ts.tag.${k}` as 'ts.tag.normal']}</dt><dd>{t['h2.times'](n)}</dd></div>
            ))}
            {Object.entries(data.actions).map(([k, n]) => (
              <div key={k}><dt>{t[`act.${k}` as 'act.other']}</dt><dd>{t['h2.times'](n)}</dd></div>
            ))}
            {Object.keys(data.context_tags).length + Object.keys(data.actions).length === 0 && (
              <div><dt>{t['h2.nothing']}</dt><dd>–</dd></div>
            )}
          </dl>
        </section>
      </div>
      <div className="two-col two-col-even">
        <SignalsPanel />
        <ThlPanel />
      </div>
      <p className="form-hint">{t['h2.footer']}</p>
    </>
  );
}

export function WindowCell({ w }: { w: Window | null }) {
  const { t, num } = useI18n();
  if (!w) return <span className="text-muted">–</span>;
  if (w.status === 'ok') {
    const b = bandOf(w.mean);
    return (
      <span className="win-cell">
        <strong>{num(w.mean)}/5</strong> <Pill kind={b.key}>{t[`band.${b.key}`]}</Pill>
        <span className="text-muted"> · {t['h2.n'](w.total)}</span>
      </span>
    );
  }
  if (w.total === 0) return <span className="text-muted">{t['h2.noAnswers']}</span>;
  return <span className="text-muted">{t[`h2.st.${w.status}` as 'h2.st.insufficient']}</span>;
}
